import type { SourceMeta } from '../components/upload/SourceMetaForm';
import type {
  ExtractedFact,
  FactCheckItem,
  FactCheckItemStatus,
  FactCheckItemType,
  VerificationResult,
} from '../types';
import { apiRequest, parseApiResponse } from '../utils/api';

// ===== エラーハンドリング =====
class FactCheckItemServiceError extends Error {
  public code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'FactCheckItemServiceError';
    this.code = code;
  }
}

// ===== ファクトチェックアイテム CRUD =====

export const createFactCheckItem = async (itemData: {
  workspaceId?: string | null;
  type: FactCheckItemType;
  title: string;
  description?: string;
  fileName?: string;
  fileUrl?: string;
  blobName?: string;
  sourceUrl?: string;
  status?: FactCheckItemStatus;
  /** 資料メタ情報。抽出プロンプトの文脈補完に使う（動画はここから読む） */
  sourceMeta?: SourceMeta;
}): Promise<FactCheckItem> => {
  try {
    const response = await apiRequest('/api/factcheck-items', {
      method: 'POST',
      body: JSON.stringify(itemData),
    });

    if (!response.ok) {
      throw new Error(`Failed to create item: ${response.status}`);
    }

    const data = await parseApiResponse<FactCheckItem>(response);
    if (!data) throw new Error('Invalid response');
    return data;
  } catch (error) {
    console.error('❌ Error creating factcheck item:', error);
    throw new FactCheckItemServiceError('ファクトチェックアイテムの作成に失敗しました');
  }
};

export const getFactCheckItem = async (itemId: string): Promise<FactCheckItem | null> => {
  try {
    const response = await apiRequest(`/api/factcheck-items/${itemId}`, {
      method: 'GET',
    });

    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Failed to get item: ${response.status}`);

    const data = await parseApiResponse<FactCheckItem>(response);
    return data || null;
  } catch (error) {
    console.error('Error getting factcheck item:', error);
    throw new FactCheckItemServiceError('ファクトチェックアイテムの取得に失敗しました');
  }
};

export const getItemVerificationResults = async (itemId: string): Promise<VerificationResult[]> => {
  try {
    const response = await apiRequest(`/api/factcheck-items/${itemId}/results`, {
      method: 'GET',
    });

    if (!response.ok) throw new Error(`Failed to get results: ${response.status}`);

    const result = await parseApiResponse<{ success: boolean; data: VerificationResult[] }>(
      response,
    );
    return result?.data || [];
  } catch (error) {
    console.error('Error getting item verification results:', error);
    throw new FactCheckItemServiceError('検証結果の取得に失敗しました');
  }
};

export const getPersonalItems = async (): Promise<FactCheckItem[]> => {
  try {
    const response = await apiRequest('/api/factcheck-items', {
      method: 'GET',
    });

    if (!response.ok) throw new Error(`Failed to get personal items: ${response.status}`);

    const result = await parseApiResponse<{ success: boolean; data: FactCheckItem[] }>(response);
    return result?.data || [];
  } catch (error) {
    console.error('Error getting personal items:', error);
    throw new FactCheckItemServiceError('個人アイテムの取得に失敗しました');
  }
};

export const getWorkspaceItems = async (workspaceId: string): Promise<FactCheckItem[]> => {
  try {
    const response = await apiRequest(`/api/factcheck-items?workspaceId=${workspaceId}`, {
      method: 'GET',
    });

    if (!response.ok) throw new Error(`Failed to get workspace items: ${response.status}`);

    const result = await parseApiResponse<{ success: boolean; data: FactCheckItem[] }>(response);
    return result?.data || [];
  } catch (error) {
    console.error('Error getting workspace items:', error);
    throw new FactCheckItemServiceError('案件アイテムの取得に失敗しました');
  }
};

export const updateFactCheckItem = async (
  itemId: string,
  updates: Partial<Omit<FactCheckItem, 'id' | 'createdBy' | 'createdAt'>>,
): Promise<void> => {
  try {
    const response = await apiRequest(`/api/factcheck-items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    if (!response.ok) throw new Error(`Failed to update item: ${response.status}`);
  } catch (error) {
    console.error('Error updating factcheck item:', error);
    throw new FactCheckItemServiceError('ファクトチェックアイテムの更新に失敗しました');
  }
};

export const deleteFactCheckItem = async (itemId: string): Promise<void> => {
  try {
    const response = await apiRequest(`/api/factcheck-items/${itemId}`, {
      method: 'DELETE',
    });

    if (!response.ok) throw new Error(`Failed to delete item: ${response.status}`);
  } catch (error) {
    console.error('Error deleting factcheck item:', error);
    throw new FactCheckItemServiceError('ファクトチェックアイテムの削除に失敗しました');
  }
};

// ===== ステータス更新 =====

export const updateItemStatus = async (
  itemId: string,
  status: FactCheckItemStatus,
): Promise<void> => {
  try {
    await updateFactCheckItem(itemId, { status });
  } catch (error) {
    console.error('Error updating item status:', error);
    throw new FactCheckItemServiceError('ステータスの更新に失敗しました');
  }
};

// ===== ファクト・検証結果の更新 =====

export const updateItemFacts = async (itemId: string, facts: ExtractedFact[]): Promise<void> => {
  try {
    await updateFactCheckItem(itemId, {
      facts,
      status: 'extracted',
    });
  } catch (error) {
    console.error('Error updating item facts:', error);
    throw new FactCheckItemServiceError('ファクトの更新に失敗しました');
  }
};

export const updateSelectedFactIndices = async (
  itemId: string,
  selectedFactIndices: number[],
): Promise<void> => {
  try {
    await updateFactCheckItem(itemId, { selectedFactIndices });
  } catch (error) {
    console.error('Error updating selected fact indices:', error);
    throw new FactCheckItemServiceError('選択ファクトの更新に失敗しました');
  }
};

export const updateVerificationResults = async (
  itemId: string,
  verification_results: VerificationResult[],
  status: FactCheckItemStatus = 'completed',
): Promise<void> => {
  try {
    await updateFactCheckItem(itemId, {
      verification_results,
      status,
    });
  } catch (error) {
    console.error('Error updating verification results:', error);
    throw new FactCheckItemServiceError('検証結果の更新に失敗しました');
  }
};

/**
 * 検証結果を1件だけ上書き（再検証の永続化用）。
 * 表示の読み出し元と同じサブコレクション(verification_results/{index})を更新するため、
 * リロード後も再検証結果が保持される。
 */
export const updateItemVerificationResult = async (
  itemId: string,
  index: number,
  result: VerificationResult,
): Promise<void> => {
  try {
    const response = await apiRequest(`/api/factcheck-items/${itemId}/results/${index}`, {
      method: 'PUT',
      body: JSON.stringify(result),
    });

    if (!response.ok) throw new Error(`Failed to update result: ${response.status}`);
  } catch (error) {
    console.error('Error updating item verification result:', error);
    throw new FactCheckItemServiceError('検証結果の更新に失敗しました');
  }
};

// ===== 担当者割り当て =====

export const assignItem = async (itemId: string, assigneeId: string | null): Promise<void> => {
  try {
    await updateFactCheckItem(itemId, { assigneeId: assigneeId || undefined });
  } catch (error) {
    console.error('Error assigning item:', error);
    throw new FactCheckItemServiceError('担当者の割り当てに失敗しました');
  }
};

// ===== 案件への共有 =====

/**
 * アイテムの検証結果を、どの案件にどれだけ見せるかを切り替える。
 *
 * アイテム自体は本人のものとして残り、案件には「この結果を見せている」という
 * 情報だけが付く。両方 false にすると、その案件からは見えなくなる。
 */
export const updateItemShare = async (
  itemId: string,
  workspaceId: string,
  share: { factCheck: boolean; compliance: boolean },
): Promise<void> => {
  try {
    await apiRequest(`/api/factcheck-items/${itemId}/share`, {
      method: 'PUT',
      body: JSON.stringify({ workspaceId, ...share }),
    });
  } catch (error) {
    console.error('Error updating item share:', error);
    throw new FactCheckItemServiceError('共有の変更に失敗しました');
  }
};

// ===== 検索・フィルタ（フロントエンド側で処理を継続） =====

export const searchItems = async (
  searchQuery: string,
  workspaceId?: string | null,
): Promise<FactCheckItem[]> => {
  try {
    let items: FactCheckItem[];

    if (workspaceId === null) {
      items = await getPersonalItems();
    } else if (workspaceId) {
      items = await getWorkspaceItems(workspaceId);
    } else {
      // 便宜上、個人アイテムを返す（または必要に応じてAPI拡張）
      items = await getPersonalItems();
    }

    const lowerQuery = searchQuery.toLowerCase();

    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery) ||
        item.fileName?.toLowerCase().includes(lowerQuery),
    );
  } catch (error) {
    console.error('Error searching items:', error);
    throw new FactCheckItemServiceError('アイテムの検索に失敗しました');
  }
};

export const filterItemsByStatus = async (
  status: FactCheckItemStatus | 'all',
  workspaceId?: string | null,
): Promise<FactCheckItem[]> => {
  try {
    let items: FactCheckItem[];

    if (workspaceId === null) {
      items = await getPersonalItems();
    } else if (workspaceId) {
      items = await getWorkspaceItems(workspaceId);
    } else {
      items = await getPersonalItems();
    }

    if (status === 'all') {
      return items;
    }

    return items.filter((item) => item.status === status);
  } catch (error) {
    console.error('Error filtering items by status:', error);
    throw new FactCheckItemServiceError('アイテムのフィルタリングに失敗しました');
  }
};
